import {
  Button,
  Checkbox,
  FloatButton,
  Form,
  Image,
  Input,
  message,
  Select,
  Spin,
  Switch,
  Upload,
  UploadProps,
} from "antd";
import { RcFile } from "antd/lib/upload";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import uploadImage from "./../FileUpload/upload.png";
import { HomeOutlined, LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import countryCodes from "./conutrycodes.json";

export const FileUploads = () => {
  const [newFile, setNewFile] = useState<RcFile | null>(null);
  const [imageFile, setImageFile] = useState<RcFile | null>(null);
  const [endPoint, setEndPoint] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [customSearchResult, setCustomSearchResult] = useState<any>(null);
  const [spectralScore, setSpectralScore] = useState<{
    score: number;
    message: string;
    reason?: string;
  } | null>(null);
  const [awaitingResult, setAwaitingResult] = useState<boolean>(false);
  const [api, setApi] = useState<null | {
    key: string;
    url: string;
    search: string;
    analyse: string;
    customSearch: string;
  }>(null);
  const [placeholder, setPlaceholder] = useState<string>(
    "File name or title..."
  );
  const [searchForm] = Form.useForm();
  const [spectralForm] = Form.useForm();
  const [customSearchForm] = Form.useForm();
  const nav = useNavigate();

  useEffect(() => {
    getDoc(doc(db, "socket/gfdjhkhfdldrjg"))
      .then((snapDoc) => {
        setApi(
          () =>
            ({ ...snapDoc.data() } as {
              key: string;
              url: string;
              search: string;
              analyse: string;
              customSearch: string;
            })
        );
      })
      .then(() => message.info("Connection Info Received."))
      .catch((err) => console.log(err));
  }, []);

  const parseJsonRcFile = (file: RcFile): Promise<Record<string, any>> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // Handle file reading
      reader.onload = () => {
        try {
          // Parse JSON content
          const jsonData = JSON.parse(reader.result as string);
          resolve(jsonData);
        } catch (error: any) {
          reject(new Error("Failed to parse JSON file: " + error.message));
        }
      };

      // Handle file reading errors
      reader.onerror = () => {
        reject(new Error("File reading failed."));
      };

      // Read the file as text
      reader.readAsText(file);
    });
  };

  const props: UploadProps = {
    beforeUpload: (file: RcFile) => {
      console.log(file.type);
      const isCorrectFileType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/jpg" ||
        file.type === "audio/mpeg" ||
        file.type === "audio/wav" ||
        file.type === "audio/ogg" ||
        file.type === "application/pdf" ||
        file.type === "audio/mp4" ||
        file.type === "audio/webm" ||
        file.type === "audio/3gp" ||
        file.type === "audio/3gpp" ||
        file.type === "audio/m4a" ||
        file.type === "audio/aac" ||
        file.type === "audio/x-m4a" ||
        file.type === "video/mp4" ||
        file.type === "video/ogg" ||
        file.type === "video/webm" ||
        file.type === "application/json";

      if (!isCorrectFileType) {
        message.error("Sorry, file type not supported");
      } else {
        //setImageName(file.name);
        setNewFile(file);
      }

      return isCorrectFileType;
    },
    accept:
      "image/png, image/jpg, image/jpeg, application/json, application/pdf, audio/ogg, audio/wav, audio/mpeg, audio/mp4, audio/webm, audio/x-m4a, audio/3gp, audio/3gpp ,audio/m4a, audio/aac, video/mp4, video/ogg, video/webm",
    customRequest: async () => {
      if (newFile) {
        try {
          const jsonData = await parseJsonRcFile(newFile);
          console.log("Parsed JSON Data:", jsonData);

          setUploading(true);

          if (!api?.key || !api.url) return;
          console.log(`Fetch via ${endPoint}`);
          fetch(api?.url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: api?.key,
            },
            body: JSON.stringify({
              data: jsonData,
            }),
          })
            .then(async (res) => {
              const result = await res.text();
              message.success(result);
              setUploading(false);
              //setEndPoint(null);
              setNewFile(null);
              //setPlaceholder("Endpoint...");
            })
            .catch((err) => {
              setUploading(false);
              setEndPoint(null);
              setNewFile(null);
              setPlaceholder("Error at endpoint...");
              message.error("Failed to upload");
              console.error(err);
            });
        } catch (error: any) {
          console.error("Error:", error.message);
        }
      }
    },
    maxCount: 1,
    showUploadList: false,
  };

  const convertRcFileToBase64 = (file: RcFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const imageProps: UploadProps = {
    beforeUpload: (file: RcFile) => {
      const isCorrectFileType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/jpg";
      if (!isCorrectFileType) {
        message.error("Sorry, file type not supported");
      } else {
        setImageFile(file);
      }
      return isCorrectFileType;
    },
    accept: "image/png, image/jpg, image/jpeg",
    maxCount: 1,
    showUploadList: false,
    customRequest: async () => {
      if (imageFile) {
        try {
          const base64 = await convertRcFileToBase64(imageFile);

          initiateSpectralAnalysis(base64 as string);
        } catch (error: any) {
          message.error("Error");
        }
      }
    },
  };

  const initiateSearch = () => {
    searchForm.validateFields().then((values) => {
      if (!api?.key || !api.search) return;
      console.log(`Initiate search`);
      fetch(api?.search, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: api?.key,
        },
        body: JSON.stringify({
          data: JSON.stringify(values),
        }),
      })
        .then(async (res) => {
          const result = await res.json();
          message.success(result.message);
        })
        .catch((err) => {
          message.error("Failed to initiate search");
          console.error(err);
        });
    });
  };

  const initiateSpectralAnalysis = (image: string) => {
    spectralForm.validateFields().then((values) => {
      if (!api?.key || !api.analyse) return;
      message.info("Initiating spectral analysis...");
      setAwaitingResult(true);
      fetch(api?.analyse, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: api?.key,
        },
        body: JSON.stringify({
          data: JSON.stringify({ ...values, condition: image }),
        }),
      })
        .then(async (res) => {
          const result = await res.json();
          setSpectralScore(result);
          setAwaitingResult(false);
          message.success(result.message);
        })
        .catch((err) => {
          message.error("Failed to return score");
          console.error(err);
        });
    });
  };

  const handleCustomSearch = () => {
    customSearchForm.validateFields().then((values) => {
      if (!api?.key || !api?.customSearch) return;
      console.log(`Initiate search`);
      fetch(api?.customSearch, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: api?.key,
        },
        body: JSON.stringify({
          data: JSON.stringify(values),
        }),
      })
        .then(async (res) => {
          const result = await res.json();
          if (result.message === "Success") {
            setCustomSearchResult({ data: result.data, type: "request" });
            console.log({ result });
            message.success(result.message);
          } else {
            message.error(result.message);
          }
        })
        .catch((err) => {
          message.error("Failed to initiate search");
        });
    });
  };

  const dateOptions = [
    { label: "One day", value: "1d" },
    { label: "One week", value: "7d" },
    { label: "One month", value: "1m" },
    { label: "Three months", value: "3m" },
    { label: "Six months", value: "6m" },
    { label: "One year", value: "1y" },
  ];
  const initiateCustomSearch = async () => {
    if (!api?.key || !api?.customSearch) return;
    try {
      const result = await fetch(`${api?.customSearch}-start`, {
        method: "GET",
        headers: {
          authorization: api?.key,
        },
      });

      const data = await result.json();
      console.log(data);
      message.info(data.message);
    } catch (error) {
      message.error("Failed to initiate search");
    }
  };

  const getList = async () => {
    if (!api?.key || !api?.customSearch) return;
    try {
      const result = await fetch(`${api?.customSearch}-list`, {
        method: "GET",
        headers: {
          authorization: api?.key,
        },
      });

      const data = await result.json();
      console.log(data);
      setCustomSearchResult({ data: data, type: "list" });
      message.info(data.message);
    } catch (error) {
      message.error("Failed to initiate search");
    }
  };

  const executeProgram = async () => {
    if (!api?.key || !api?.customSearch) return;
    try {
      const result = await fetch(`${api?.customSearch}-execute`, {
        method: "GET",
        headers: {
          authorization: api?.key,
        },
      });

      const data = await result.json();
      console.log(data);
      message.info(data.message);
    } catch (error) {
      message.error("Failed to execute program");
    }
  };

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: "15",
        backgroundColor: "rgba(0,0,0,0.9)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ width: "70%", maxWidth: "300px" }}>
          <div
            style={{
              color: "white",
              marginBottom: "1rem",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Search
          </div>
          <Form form={searchForm}>
            <Form.Item
              name="target"
              rules={[
                {
                  required: true,
                  message: "Please input a target name!",
                },
              ]}
            >
              <Input placeholder="Target name" />
            </Form.Item>
          </Form>
          <Button
            type="primary"
            onClick={initiateSearch}
            style={{ marginTop: "10px", width: "100%" }}
          >
            Search
          </Button>
        </div>
        <div>
          <div
            style={{
              color: "white",
              paddingBottom: "1rem",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Networks
          </div>
          {uploading ? (
            <div>
              <Spin indicator={<LoadingOutlined spin />} size="large" />
              <div style={{ color: "white" }}>Uploading...</div>
            </div>
          ) : (
            <Upload {...props}>
              <Button type="text">
                <Image
                  preview={false}
                  src={uploadImage}
                  style={{ width: "100%" }}
                />
              </Button>
            </Upload>
          )}
        </div>
        <div style={{ width: "70%", maxWidth: "400px" }}>
          <div
            style={{
              color: "white",
              paddingBottom: "1rem",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Left-Right Spectral Analysis
          </div>

          <Upload {...imageProps}>
            <Button type="text">
              <Image
                preview={false}
                src={uploadImage}
                style={{ width: "100%" }}
              />
            </Button>
          </Upload>
          <Form form={spectralForm}>
            <Form.Item
              name="prompt"
              rules={[
                {
                  required: true,
                  message: "Please input text for analysis!",
                },
              ]}
            >
              <Input.TextArea rows={10} placeholder="Text to analyse" />
            </Form.Item>
            {/* <Form.Item name="condition">
              <Input.TextArea rows={10} placeholder="Conditions" />
            </Form.Item> */}
          </Form>
          {spectralScore && !awaitingResult && (
            <div
              onClick={() =>
                navigator.clipboard.writeText(
                  `Score: ${spectralScore.score} \n Reason: ${spectralScore.reason}`
                )
              }
              style={{ color: "white" }}
            >{`Score: ${spectralScore.score} \n Reason: ${spectralScore.reason}`}</div>
          )}
          {/* <Button
            type="primary"
            onClick={() => imageFile && initiateSpectralAnalysis(imageFile)}
            style={{ marginTop: "10px", width: "100%" }}
          >
            Analyse
          </Button> */}
        </div>
        <div style={{ width: "70%", maxWidth: "400px" }}>
          <div
            style={{
              color: "white",
              paddingBottom: "1rem",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Custom Google Search
          </div>
          <Form form={customSearchForm}>
            <Form.Item
              name="query"
              rules={[
                {
                  required: true,
                  message: "Please input text for analysis!",
                },
              ]}
            >
              <Input.TextArea rows={2} placeholder="Primary query" />
            </Form.Item>
            <Form.Item name="additional">
              <Input.TextArea rows={2} placeholder="Additional information" />
            </Form.Item>
            <Form.Item name="date">
              <Select
                options={dateOptions}
                defaultValue={dateOptions[0].value}
              />
            </Form.Item>
            <Form.Item name="exact">
              <Switch checkedChildren="Exact" unCheckedChildren="Fuzzy" />
            </Form.Item>{" "}
            <Form.Item name="loc">
              <Select
                options={countryCodes}
                showSearch
                placeholder="Search location"
              />
            </Form.Item>
          </Form>
          <Button
            type="primary"
            onClick={handleCustomSearch}
            style={{ marginTop: "10px", width: "100%" }}
          >
            Get results
          </Button>
          <Button
            type="primary"
            onClick={initiateCustomSearch}
            style={{ marginTop: "10px", width: "100%" }}
          >
            Start
          </Button>
          <Button
            type="primary"
            onClick={getList}
            style={{ marginTop: "10px", width: "100%" }}
          >
            List
          </Button>
          {customSearchResult && customSearchResult.type === "request" && (
            <div
              style={{ color: "white", maxHeight: "300px", overflow: "auto" }}
            >
              {customSearchResult.data.map((item: any, idx: number) => {
                return (
                  <div key={idx}>
                    <div>
                      {idx + 1}. {item.title}
                    </div>
                    <div>{item.snippet}</div>
                    <Button type="text" onClick={() => window.open(item.link)}>
                      Link
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          {customSearchResult && customSearchResult.type === "list" && (
            <div
              style={{ color: "white", maxHeight: "300px", overflow: "auto" }}
            >
              {customSearchResult.data.map((item: any, idx: number) => {
                return (
                  <div key={idx}>
                    <div>
                      {idx + 1}. {item.title}
                    </div>
                    <div>{item.snippet}</div>
                    <div>{item.pageText}</div>
                    <Button type="text" onClick={() => window.open(item.link)}>
                      Link
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ width: "70%", maxWidth: "400px" }}>
          <div
            style={{
              color: "white",
              paddingBottom: "1rem",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            Execute Program
          </div>
          <Button
            type="primary"
            onClick={() => {
              executeProgram();
            }}
          >
            Execute
          </Button>
        </div>
      </div>
      <FloatButton
        icon={<HomeOutlined />}
        onClick={() => {
          nav("/ai-tools");
        }}
      />
    </div>
  );
};
